(() => {
  'use strict';
  if (customElements.get('product-gallery')) return;
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = [...this.querySelectorAll('[data-media-index]')];
      this.thumbnails = [...this.querySelectorAll('[data-gallery-index]:not([data-gallery-dot])')];
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      this.index = Number(this.dataset.selectedIndex || 0);
      this.fixedStage = this.hasAttribute('data-static-stage');
      this.stageIndex = this.index;
      this.dialog = this.querySelector('dialog');
      const dialogOrder = (this.dataset.galleryDialogOrder || '').split(',').filter(value => value.trim() !== '').map(Number);
      this.dialogOrder = dialogOrder.length === this.slides.length && new Set(dialogOrder).size === this.slides.length && dialogOrder.every(index => Number.isInteger(index) && index >= 0 && index < this.slides.length) ? dialogOrder : this.slides.map((slide, index) => index);
      this.zoomScale = 1;
      this.zoomOffset = { x: 0, y: 0 };
      this.zoomPointers = new Map();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-gallery-next]')) this.step(1, this.dialog?.contains(event.target));
        if (event.target.closest('[data-gallery-prev]')) this.step(-1, this.dialog?.contains(event.target));
        const thumbnail = event.target.closest('[data-gallery-index]');
        if (thumbnail) {
          if (this.fixedStage && !this.dialog?.contains(thumbnail)) this.open(thumbnail, Number(thumbnail.dataset.galleryIndex));
          else this.select(Number(thumbnail.dataset.galleryIndex));
        }
        const zoom = event.target.closest('[data-gallery-zoom]');
        if (zoom) this.open(zoom);
        if (event.target.closest('[data-gallery-close]') || event.target === this.dialog) this.close();
        if (event.target.closest('[data-gallery-image-zoom]') || event.target.matches('[data-gallery-expanded] img')) {
          if (this.suppressImageClick) this.suppressImageClick = false;
          else this.setZoom(this.zoomScale > 1 ? 1 : 2);
        }
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.target.closest('input,textarea,select,video,iframe,model-viewer')) return;
        if (this.fixedStage && !this.dialog?.open) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1, this.dialog?.contains(event.target)); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1, this.dialog?.contains(event.target)); }
      }, { signal });
      const stage = this.querySelector('.product-gallery__stage');
      stage?.addEventListener('touchstart', event => {
        if (this.fixedStage || event.target.closest('video,iframe,model-viewer') || event.touches.length !== 1) { this.touch = null; return; }
        this.touch = {x:event.touches[0].clientX,y:event.touches[0].clientY};
      }, { passive: true, signal });
      stage?.addEventListener('touchcancel', () => { this.touch = null; }, { passive:true,signal });
      stage?.addEventListener('touchend', event => {
        if (!this.touch || !event.changedTouches[0]) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touch.x, deltaY = touch.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(deltaX)>40 && Math.abs(deltaX)>Math.abs(deltaY)) this.select(this.index+(deltaX<0?1:-1));
      }, { passive:true,signal });
      const zoomStage = this.querySelector('[data-gallery-expanded]');
      zoomStage?.addEventListener('pointerdown', event => {
        if (!zoomStage.querySelector('img') || event.target.closest('video,iframe,model-viewer') || event.pointerType === 'mouse' && event.button !== 0) return;
        this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        zoomStage.setPointerCapture(event.pointerId);
        this.zoomStart = { x: event.clientX, y: event.clientY, offsetX: this.zoomOffset.x, offsetY: this.zoomOffset.y };
        if (this.zoomPointers.size === 2) { const [a,b] = [...this.zoomPointers.values()]; this.pinchStart = { distance: Math.hypot(a.x-b.x,a.y-b.y), scale: this.zoomScale }; }
      }, { signal });
      zoomStage?.addEventListener('pointermove', event => {
        if (!this.zoomPointers.has(event.pointerId)) return;
        this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.zoomPointers.size === 2 && this.pinchStart?.distance > 0) { const [a,b] = [...this.zoomPointers.values()]; this.suppressImageClick = true; this.setZoom(this.pinchStart.scale*Math.hypot(a.x-b.x,a.y-b.y)/this.pinchStart.distance); }
        else if (this.zoomStart) { const dx=event.clientX-this.zoomStart.x,dy=event.clientY-this.zoomStart.y;if(Math.hypot(dx,dy)>6)this.suppressImageClick=true;if(this.zoomScale>1){this.zoomOffset={x:this.zoomStart.offsetX+dx,y:this.zoomStart.offsetY+dy};this.paintZoom();} }
      }, { signal });
      const endPointer = event => {
        if (!this.zoomPointers.has(event.pointerId)) return;
        const start=this.zoomStart,wasPinch=Boolean(this.pinchStart);
        this.zoomPointers.delete(event.pointerId);
        if (this.zoomPointers.size === 0) { this.pinchStart=null;this.zoomStart=null;if(!wasPinch&&start&&this.zoomScale===1&&event.type!=='pointercancel'){const dx=event.clientX-start.x,dy=event.clientY-start.y;if(Math.abs(dx)>40&&Math.abs(dx)>Math.abs(dy)){this.suppressImageClick=true;this.step(dx<0?1:-1,true);}} }
      };
      zoomStage?.addEventListener('pointerup', endPointer, { signal });
      zoomStage?.addEventListener('pointercancel', endPointer, { signal });
      this.dialog?.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, {signal});
      this.dialog?.addEventListener('close', () => {
        if (this.dialog.open) return;
        clearTimeout(this.closeTimer);
        cancelAnimationFrame(this.openFrame);
        this.dialog.classList.remove('is-opening','is-open','is-closing');
        this.zoomPointers.clear();this.pinchStart=null;this.zoomStart=null;this.suppressImageClick=false;
        document.documentElement.classList.remove('product-gallery-open');
        const expanded = this.querySelector('[data-gallery-expanded]');
        if (expanded) { this.pauseMedia(expanded); expanded.querySelectorAll('model-viewer').forEach(model=>model.oleaViewer?.destroy()); expanded.replaceChildren(); }
        if (this.opener?.isConnected) this.opener.focus({preventScroll:true});
      }, {signal});
      this.select(this.index, false);
      if (this.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:()=>{if(this.isConnected)this.initializeModels(this);}}]);
      }
    }
    initializeModels(scope) {
      scope.querySelectorAll('model-viewer').forEach(model=>{
        if (window.Shopify?.ModelViewerUI && !model.oleaViewer) model.oleaViewer = new window.Shopify.ModelViewerUI(model);
      });
    }
    pauseMedia(scope) {
      scope.querySelectorAll('video').forEach(video=>video.pause());
      scope.querySelectorAll('iframe').forEach(frame=>{
        try {
          const origin = new URL(frame.src,location.origin).origin;
          if (['https://www.youtube.com','https://www.youtube-nocookie.com'].includes(origin)) frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}),origin);
          if (origin==='https://player.vimeo.com') frame.contentWindow?.postMessage(JSON.stringify({method:'pause'}),origin);
        } catch {}
      });
    }
    motionEnabled() { return document.body.classList.contains('animations-enabled')&&!matchMedia('(prefers-reduced-motion: reduce)').matches; }
    disconnectedCallback() {
      this.controller?.abort(); this.controller=null;
      clearTimeout(this.closeTimer); cancelAnimationFrame(this.openFrame);
      this.pauseMedia(this);
      this.querySelectorAll('model-viewer').forEach(model=>model.oleaViewer?.destroy());
      if (this.dialog) window.OleaOverlayMotion.hide(this.dialog, { immediate: true });
      document.documentElement.classList.remove('product-gallery-open');
    }
    step(direction, inDialog = false) {
      if (!inDialog) { if (!this.fixedStage) this.select(this.index + direction); return; }
      if (!this.dialogOrder.length) return;
      const position = this.dialogOrder.indexOf(this.index);
      this.select(this.dialogOrder[(position + direction + this.dialogOrder.length) % this.dialogOrder.length]);
    }
    select(index, scroll = true) {
      if (!this.slides.length || !Number.isInteger(index)) return;
      if (scroll) this.dataset.galleryInteracted='true';
      this.index = (index%this.slides.length+this.slides.length)%this.slides.length;
      if (this.fixedStage && this.dialog?.open) {
        this.querySelectorAll('[data-gallery-dialog-thumb],[data-gallery-dot]').forEach(control => {
          const active = Number(control.dataset.galleryIndex) === this.index;
          control.classList.toggle('is-active', active);
          control.setAttribute('aria-pressed', String(active));
        });
        this.querySelectorAll('[data-gallery-dialog-count]').forEach(counter => { counter.textContent = String(this.dialogOrder.indexOf(this.index)+1)+' / '+this.slides.length; });
        this.expand();
        return;
      }
      this.stageIndex = this.index;
      this.slides.forEach((slide,i)=>{
        const active=i===this.index;
        slide.hidden=!active;
        slide.classList.toggle('is-active',active);
        if (!active) this.pauseMedia(slide);
      });
      let preview=0;
      const mobileCount = Number(this.dataset.mobilePreviewCount) === 3 ? 3 : 4;
      const mobilePreviews = new Set([this.index]);
      for (let i=0; i<this.thumbnails.length && mobilePreviews.size<mobileCount; i+=1) mobilePreviews.add(i);
      this.thumbnails.forEach(thumbnail=>{
        const active=Number(thumbnail.dataset.galleryIndex)===this.index;
        thumbnail.classList.toggle('is-active',active);
        thumbnail.classList.toggle('is-mobile-preview',mobilePreviews.has(Number(thumbnail.dataset.galleryIndex)));
        thumbnail.setAttribute('aria-pressed',String(active));
        thumbnail.classList.remove('is-preview','product-gallery__thumbnail--preview-0','product-gallery__thumbnail--preview-1','product-gallery__thumbnail--preview-2');
        if (!active&&preview<3) {thumbnail.classList.add('is-preview','product-gallery__thumbnail--preview-'+preview);preview+=1;}
        const rail=thumbnail.parentElement;
        if (active&&scroll&&rail&&['auto','scroll'].includes(getComputedStyle(rail).overflowX)) rail.scrollTo({left:thumbnail.offsetLeft-rail.clientWidth/2+thumbnail.offsetWidth/2,behavior:this.motionEnabled()?'smooth':'instant'});
      });
      this.classList.add('is-gallery-ready');
      this.dots.forEach(dot=>{const active=Number(dot.dataset.galleryIndex)===this.index;dot.classList.toggle('is-active',active);dot.setAttribute('aria-pressed',String(active));});
      this.querySelectorAll('[data-gallery-count]').forEach(counter=>{counter.textContent=String(this.index+1);});
      this.querySelectorAll('[data-gallery-dialog-count]').forEach(counter=>{counter.textContent=String(this.dialogOrder.indexOf(this.index)+1)+' / '+this.slides.length;});
      if (this.dialog?.open) this.expand();
    }
    expand() {
      const current=this.slides[this.index],container=this.querySelector('[data-gallery-expanded]');
      if (!current||!container) return;
      this.setZoom(1);
      this.pauseMedia(container);
      container.querySelectorAll('model-viewer').forEach(model=>model.oleaViewer?.destroy());
      const expanded=current.cloneNode(true);
      expanded.removeAttribute('data-media-index');
      expanded.removeAttribute('data-media-id');
      expanded.removeAttribute('id');
      expanded.className='product-gallery__expanded';
      expanded.hidden=false;
      expanded.querySelectorAll('[id]').forEach(node=>node.removeAttribute('id'));
      container.replaceChildren(expanded);
      const hint=this.querySelector('[data-gallery-image-zoom]');if(hint)hint.hidden=!expanded.querySelector('img');
      this.initializeModels(container);
    }
    open(opener, index = this.stageIndex) {
      if (!this.dialog) return;
      if (this.fixedStage && Number.isInteger(index)) this.index = index;
      this.querySelectorAll('[data-gallery-dialog-thumb],[data-gallery-dot]').forEach(control => {
        const active = Number(control.dataset.galleryIndex) === this.index;
        control.classList.toggle('is-active', active);
        control.setAttribute('aria-pressed', String(active));
      });
      this.querySelectorAll('[data-gallery-dialog-count]').forEach(counter => { counter.textContent = String(this.dialogOrder.indexOf(this.index)+1)+' / '+this.slides.length; });
      clearTimeout(this.closeTimer);cancelAnimationFrame(this.openFrame);
      this.opener=opener||this.querySelector('[data-gallery-zoom]');
      this.pauseMedia(this);
      this.expand();
      document.documentElement.classList.add('product-gallery-open');
      return window.OleaOverlayMotion.show(this.dialog);
    }
    setZoom(value) {
      this.zoomScale=Math.max(1,Math.min(4,Number(value)||1));
      if(this.zoomScale===1)this.zoomOffset={x:0,y:0};
      this.paintZoom();
    }
    paintZoom() {
      const image=this.querySelector('[data-gallery-expanded] img');
      const stage=this.querySelector('[data-gallery-expanded]');
      if(image&&stage){const maxX=Math.max(0,(image.clientWidth*this.zoomScale-stage.clientWidth)/2),maxY=Math.max(0,(image.clientHeight*this.zoomScale-stage.clientHeight)/2);this.zoomOffset.x=Math.max(-maxX,Math.min(maxX,this.zoomOffset.x));this.zoomOffset.y=Math.max(-maxY,Math.min(maxY,this.zoomOffset.y));image.style.transform=`translate(${this.zoomOffset.x}px,${this.zoomOffset.y}px) scale(${this.zoomScale})`;}
      this.dialog?.classList.toggle('is-zoomed',this.zoomScale>1);
      this.querySelector('[data-gallery-image-zoom]')?.setAttribute('aria-pressed',String(this.zoomScale>1));
    }
    close() {
      if (!this.dialog?.open) return;
      clearTimeout(this.closeTimer);cancelAnimationFrame(this.openFrame);
      return window.OleaOverlayMotion.hide(this.dialog);
    }
  }
  customElements.define('product-gallery',ProductGallery);
  document.addEventListener('click',async event=>{
    const link=event.target.closest('a[data-product-family-link]');
    const activeTheme=window.Shopify?.theme;
    const previewId=new URL(location.href).searchParams.get('preview_theme_id')||(activeTheme&&['unpublished','development'].includes(activeTheme.role)?activeTheme.id:null);
    if (link&&previewId) {
      const url=new URL(link.href,location.origin);
      if(url.origin===location.origin){url.searchParams.set('preview_theme_id',String(previewId));link.href=url.href;}
    }
    const button=event.target.closest('[data-product-share]');
    if (!button) return;
    event.preventDefault();
    const url=new URL(button.dataset.shareUrl||location.pathname,location.origin);
    if(url.origin!==location.origin)return;
    const variant=new URL(location.href).searchParams.get('variant');
    url.search='';if(variant)url.searchParams.set('variant',variant);if(previewId)url.searchParams.set('preview_theme_id',String(previewId));
    try {
      if(navigator.share&&matchMedia('(pointer:coarse)').matches) await navigator.share({title:document.querySelector('.product-main h1')?.textContent.trim(),url:url.href});
      else {await window.Olea.copyText(url.href);window.Olea.announce(button.dataset.copiedLabel||window.Olea.strings.copied);}
    } catch(error) {if(error.name!=='AbortError')window.Olea.toast(window.Olea.strings.error);}
  });
})();

